import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { SOCIALS } from "../lib/socials";

const INTERESTS = ["Build — Software Engineering", "Operate — Managed Operations", "Automate — AI & Automation", "Grow — Digital Growth", "Something else"];

export const ContactModal = ({ open, onClose }) => {
  const [form, setForm] = useState({ name: "", email: "", company: "", interest: "", message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus("loading");
    setError("");
    try {
      await api.post("/enquiries", { ...form, company: form.company || null, interest: form.interest || null });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setError(err?.response?.data?.detail?.[0]?.msg || "Something went wrong. Please try again or email hello@jiyonox.com.");
    }
  };

  const close = () => {
    onClose();
    setTimeout(() => { setStatus("idle"); setForm({ name: "", email: "", company: "", interest: "", message: "" }); setError(""); }, 350);
  };

  const inputCls = "w-full bg-transparent border-b border-jx-ink/15 focus:border-jx-purple outline-none px-0 py-3 text-base text-jx-ink placeholder:text-jx-ink/35 transition-colors duration-300";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="contact-modal"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center sm:p-6 bg-jx-night/65 backdrop-blur-md"
          onClick={close}
        >
          <motion.div
            initial={{ y: 90, opacity: 0, scale: 0.98 }}
            animate={{ y: 0, opacity: 1, scale: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-xl max-h-[90vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-white p-8 sm:p-12"
          >
            <div className="absolute top-0 inset-x-0 h-1.5 jx-gradient-bg rounded-t-3xl" />
            <button data-testid="contact-modal-close" onClick={close} className="absolute top-5 right-5 p-2 rounded-full bg-jx-mist hover:bg-jx-purple/10 transition-colors" aria-label="Close">
              <X className="w-5 h-5 text-jx-ink" />
            </button>

            {status === "success" ? (
              <div className="py-10 text-center" data-testid="contact-success">
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 200, damping: 14 }} className="mx-auto w-16 h-16 rounded-full jx-gradient-bg flex items-center justify-center">
                  <CheckCircle2 className="w-8 h-8 text-white" />
                </motion.div>
                <h3 className="mt-6 font-display font-extrabold text-3xl tracking-tighter text-jx-ink">Message received.</h3>
                <p className="mt-3 text-jx-ink/60">Thank you, {form.name.split(" ")[0]}. Our team will get back to you shortly. You can also reach us anytime at hello@jiyonox.com.</p>
                <button data-testid="contact-done-button" onClick={close} className="mt-8 rounded-full bg-jx-ink text-white px-7 py-3 font-medium hover:bg-jx-purple transition-colors duration-300">Done</button>
              </div>
            ) : (
              <>
                <p className="jx-overline text-jx-purple">Start a Conversation</p>
                <h3 className="mt-3 font-display font-extrabold text-3xl tracking-tighter text-jx-ink">Tell us what you're building.</h3>
                <form onSubmit={submit} className="mt-8 flex flex-col gap-5" data-testid="contact-form">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <input required data-testid="contact-input-name" value={form.name} onChange={set("name")} placeholder="Your name *" className={inputCls} />
                    <input required type="email" data-testid="contact-input-email" value={form.email} onChange={set("email")} placeholder="Work email *" className={inputCls} />
                  </div>
                  <input data-testid="contact-input-company" value={form.company} onChange={set("company")} placeholder="Company" className={inputCls} />
                  <select data-testid="contact-input-interest" value={form.interest} onChange={set("interest")} className={`${inputCls} ${form.interest ? "" : "text-jx-ink/35"} bg-white`}>
                    <option value="">What are you interested in?</option>
                    {INTERESTS.map((i) => <option key={i} value={i}>{i}</option>)}
                  </select>
                  <textarea required rows={4} data-testid="contact-input-message" value={form.message} onChange={set("message")} placeholder="What are you trying to build, improve or automate? *" className={`${inputCls} resize-none`} />
                  {status === "error" && <p data-testid="contact-error" className="text-sm text-red-600">{error}</p>}
                  <button
                    type="submit"
                    disabled={status === "loading"}
                    data-testid="contact-submit-button"
                    className="group mt-2 inline-flex items-center justify-center gap-3 rounded-full jx-gradient-bg text-white font-medium px-8 py-4 transition-[transform,box-shadow] duration-300 hover:scale-[1.02] active:scale-95 disabled:opacity-60 shadow-[0_14px_36px_rgba(209,45,140,0.3)]"
                  >
                    {status === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send Message <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" /></>}
                  </button>
                  <p className="text-xs text-jx-ink/40 text-center">Prefer email? <a href="mailto:hello@jiyonox.com" className="text-jx-purple font-medium">hello@jiyonox.com</a></p>
                  <div className="mt-1 flex items-center justify-center gap-2.5" data-testid="contact-socials">
                    {SOCIALS.map((s) => (
                      <a
                        key={s.label}
                        href={s.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        aria-label={s.label}
                        data-testid={`contact-social-${s.label.toLowerCase()}`}
                        className="w-9 h-9 rounded-full border border-jx-ink/10 flex items-center justify-center text-jx-ink/45 hover:text-white hover:bg-jx-purple hover:border-jx-purple transition-[color,border-color,background-color] duration-300"
                      >
                        <s.Icon className="w-3.5 h-3.5" />
                      </a>
                    ))}
                  </div>
                </form>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
